/**
 * RIQMetricLineChart is intended to draw one or more linked line charts. If you have more than one line chart, then hovering a tooltip
 * on any of the line charts will show the tooltip on all of the line charts.
 */

import React, {
    useRef
} from 'react';
import PropTypes from 'prop-types';

import {
    ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine,
    AreaChart, Area
} from 'recharts';

import XAxisTick from './XAxisTick.jsx';

import {
    useIsExporting
} from '../hooks/useExport.js';

import {
    useHoverableTooltip,
    ChartTooltip
} from './ChartTooltip.jsx';

import Label from './Label.jsx';

const DEFAULT_LINE_CHART_HEIGHT = 219;
const MARGIN_TOP_PX = 5;

function calculateTooltipParams(activeTooltipIndex, dataByInterval, chartConfig, lineRef, tooltipPlacement, tooltipContent, onTooltipFocusChange) {
    let tooltipPoint;
    let referenceLineWidth = 0;
    if (lineRef.current && activeTooltipIndex !== -1) {
        tooltipPoint = lineRef?.current?.state.formattedGraphicalItems?.[0]?.props?.points?.[activeTooltipIndex];
        if (tooltipPlacement === 'top') {
            tooltipPoint = _.defaults({
                y: MARGIN_TOP_PX
            }, tooltipPoint);
        }

        const previousPoint = (activeTooltipIndex === 0 ? 0 : lineRef?.current?.state.formattedGraphicalItems?.[0]?.props?.points?.[activeTooltipIndex - 1]);
        const nextPoint = (activeTooltipIndex === dataByInterval.length - 1 ?
            lineRef?.current?.state.formattedGraphicalItems?.[0]?.props?.points?.[activeTooltipIndex] :
            lineRef?.current?.state.formattedGraphicalItems?.[0]?.props?.points?.[activeTooltipIndex + 1]);

        if (tooltipPoint && nextPoint) {
            // If this is the first or last point, we need special handling to make sure the reference line is wide enough.
            if (tooltipPoint.x === nextPoint.x) {
                referenceLineWidth = tooltipPoint.x - previousPoint.x;
            } else if (!previousPoint) {
                referenceLineWidth = (nextPoint.x - tooltipPoint.x);
            } else {
                referenceLineWidth = (tooltipPoint.x - previousPoint.x) / 2 + (nextPoint.x - tooltipPoint.x) / 2;
            }
        }
    }

    if (tooltipContent) {
        tooltipContent = React.cloneElement(
            tooltipContent,
            {
                lineConfig: chartConfig,
                datum: dataByInterval[activeTooltipIndex],
                placement: tooltipPlacement || 'top',
                onTooltipFocusChange: onTooltipFocusChange
            }
        );
    }

    return {
        tooltipPoint,
        referenceLineWidth,
        tooltipContent
    };
}

function findValuesDomain(chartConfig, dataByInterval, includeFullDataPoints, includePartialDataPoints) {

    const allValues = _.reduce(dataByInterval, (memo, datum) => {

        const values = _.flatMap(chartConfig.metrics, metric => {

            const datumValues = [];
            if (includeFullDataPoints && !_.isNull(datum[metric.dataKey])) {
                datumValues.push(datum[metric.dataKey]);
            }

            if (includePartialDataPoints && !_.isNull(datum[`partial_${metric.dataKey}`])) {
                datumValues.push(datum[`partial_${metric.dataKey}`]);
            }

            return datumValues;
        });

        return memo.concat(values);
    }, []);
    const highestPositiveValue = Math.max(0, _.max(allValues));
    const lowestNegativeValue = Math.min(0, _.min(allValues));

    return {
        highestPositiveValue,
        lowestNegativeValue
    };
}

function calcPctPositive(chartConfig, dataByInterval, includeFullDataPoints, includePartialDataPoints) {

    const {highestPositiveValue, lowestNegativeValue} = findValuesDomain(chartConfig, dataByInterval, includeFullDataPoints, includePartialDataPoints);

    const totalRange = highestPositiveValue + Math.abs(lowestNegativeValue);
    if (totalRange === 0) {
        // This shouldn't really happen, but being defensive just in case.
        return 0;
    }

    const pctPositive = highestPositiveValue / totalRange;
    return pctPositive;
}

function _MetricLineChart({chartKey, lineChartHeight, dataByInterval, chartConfig, tooltipPlacement, activeTooltipIndex, onPointActivated, onTooltipFocusChange, tooltipContent, isExporting}) {

    const lineRef = useRef();
    function onMouseMove() {
        const lineState = lineRef?.current?.state;
        if (lineState) {
            onPointActivated(lineState.activeTooltipIndex, lineState.isTooltipActive);
        }
    }

    const tooltipParams = calculateTooltipParams(activeTooltipIndex, dataByInterval, chartConfig, lineRef, tooltipPlacement, tooltipContent, onTooltipFocusChange);
    const {tooltipPoint, referenceLineWidth} = tooltipParams;

    tooltipContent = tooltipParams.tooltipContent;

    const pctPositiveFullPeriods = calcPctPositive(chartConfig, dataByInterval, true, false);

    const primaryMetric = chartConfig.metrics[0];
    const yAxisId = primaryMetric.dataKey;
    const hasAreaChart = _.some(chartConfig.metrics, 'showAreaChart');
    const hasNonAreaChart = _.some(chartConfig.metrics, metric => !metric.showAreaChart);

    let ChartComponent = ComposedChart;
    const yaxisProps = {
        ...chartConfig.yaxisProps
    };

    if (hasAreaChart) {
        if (!hasNonAreaChart) {
            ChartComponent = AreaChart;
        }

        let domainMax = chartConfig.domainMax;
        if (!_.isFinite(domainMax)) {
            const {highestPositiveValue, lowestNegativeValue} = findValuesDomain(chartConfig, dataByInterval, true, true);

            const maxValue = Math.max(highestPositiveValue, Math.abs(lowestNegativeValue));
            domainMax = Math.round(100 * maxValue * 1.1) / 100;
            if (chartConfig.maxYTickValue) {
                domainMax = Math.min(domainMax, chartConfig.maxYTickValue);
            }
        }

        // the recharts tick calculation doesn't do what we want for highly asymmetric pos/negative values in the area chart, but
        // for this chart it's easy enough to calculate it ourselves.
        yaxisProps.domain = [-domainMax, domainMax];
        yaxisProps.ticks = [-domainMax, -domainMax / 2, 0, domainMax / 2, domainMax];
    } else {
        if (!hasAreaChart) {
            ChartComponent = LineChart;
        }
    }

    return (
        <div key={chartConfig.chartName}
            onMouseLeave={() => onPointActivated(-1, false)}
        >
            <Label description={chartConfig.description}>{chartConfig.chartName}</Label>
            <ResponsiveContainer
                key={chartConfig.chartName}
                width='100%'
                height={lineChartHeight}
            >
                <ChartComponent
                    syncId={chartKey}
                    data={dataByInterval}
                    // the margin is necessary to prevent clipping the dots if a line is at the top or right side of the chart.
                    margin={{
                        top: MARGIN_TOP_PX,
                        right: 5,
                        left: 0,
                        bottom: 0
                    }}
                    onMouseMove={onMouseMove}
                    ref={lineRef}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke='#d3d3d3'
                        fill='none'
                        shapeRendering='crispEdges'
                    />
                    <XAxis
                        stroke='#d3d3d3'
                        fill='none'
                        shapeRendering='crispEdges'
                        dataKey='idx'
                        interval='preserveStartEnd'
                        type='number'
                        tickCount={Math.min(dataByInterval.length, 6)}
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(tick) => dataByInterval[tick].xValue}
                        tick={<XAxisTick/>}
                    />
                    <YAxis
                        stroke='#d3d3d3'
                        fill='none'
                        shapeRendering='crispEdges'
                        yAxisId={yAxisId}
                        tick={{
                            fill: '#4c535e',
                            fontSize: '12px'
                        }}
                        tickFormatter={primaryMetric.abbreviatedFormatter}
                        {...yaxisProps}
                    />

                    <ChartTooltip
                        // this key will force a re-render on active change, which gives the tooltip lib a chance to remeasure. See #34320
                        key={`tooltip-${activeTooltipIndex}`}
                        tooltipPoint={tooltipPoint}
                        tooltipContent={tooltipContent}
                    />
                    {/* This reference line is the grey cursor line under the tooltip */}
                    <ReferenceLine
                        x={activeTooltipIndex < 0 ? 0 : activeTooltipIndex}
                        yAxisId={yAxisId}
                        stroke='rgba(211, 211, 211, 0.4)'
                        strokeWidth={activeTooltipIndex < 0 ? 0 : referenceLineWidth}
                        ifOverflow='hidden'
                    />

                    {chartConfig.colorRules &&
                        <defs>
                            <linearGradient
                                id={`${chartKey}ColorGradient`}
                                key={`${chartKey}ColorGradient`}
                                x1='0%'
                                y1='0%'
                                x2='0%'
                                y2='100%'>
                                <stop
                                    offset={`0%`}
                                    stopColor={chartConfig.colorRules.positiveColor}
                                    stopOpacity={1}
                                />
                                <stop
                                    offset={`${100 * pctPositiveFullPeriods}%`}
                                    stopColor={chartConfig.colorRules.positiveColor}
                                    stopOpacity={1}
                                />
                                <stop
                                    offset={`${100 * pctPositiveFullPeriods}%`}
                                    stopColor={chartConfig.colorRules.negativeColor}
                                    stopOpacity={1}
                                />
                                <stop
                                    offset={`100%`}
                                    stopColor={chartConfig.colorRules.negativeColor}
                                    stopOpacity={1}
                                />
                            </linearGradient>
                        </defs>
                    }

                    {_.map(chartConfig.metrics, ({showAreaChart, dataKey, lineColor}, idx) => {
                        if (showAreaChart) {
                            return (
                                <Area
                                    key={`area-${idx}`}
                                    dataKey={dataKey}
                                    yAxisId={yAxisId}
                                    dot={false}
                                    stroke={chartConfig.colorRules ? `url(#${chartKey}ColorGradient)` : lineColor}
                                    fill={chartConfig.colorRules ? `url(#${chartKey}ColorGradient)` : lineColor}
                                    activeDot={false}
                                    legendType='plainline'
                                />
                            );
                        } else {
                            return (
                                <Line
                                    key={`line-${idx}`}
                                    dataKey={dataKey}
                                    yAxisId={yAxisId}
                                    stroke={lineColor}
                                    dot={{
                                        r: 1,
                                        stroke: lineColor,
                                        strokeWidth: 2,
                                        fill: lineColor
                                    }}
                                    activeDot={false}
                                    legendType='plainline'
                                />
                            );
                        }
                    })}
                </ChartComponent>
            </ResponsiveContainer>
        </div>
    );
}

RIQMetricLineChart.collateMetricResults = function (interval, metricRequests, metricResults, metricKeys, metricKeyFns, timeDimensionKey = 0) {

    if (_.isEmpty(metricKeys) || _.isEmpty(metricResults)) {
        return [];
    }

    const representativeRequestName = _.head(metricKeys);
    const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(_.pick(metricResults, metricKeys));
    const timeDimension = RivalIQ.metrics.new.MetricRequest.getTimeDimension(metricRequests[representativeRequestName]);
    const timezone = metricRequests[representativeRequestName].filters.timePeriod.timezone;
    const dataByInterval = _.map(collatedResults, (result, idx) => {
        const timeDimensionValue = _.get(result, [representativeRequestName, timeDimensionKey]);
        const newStartDate = moment.tz(timeDimensionValue, timezone);
        const newEndDate = moment.tz(newStartDate, timezone).endOf(interval);

        let toolTipTitle = newStartDate.format('MMM D, YYYY');
        if (interval !== 'day') {
            toolTipTitle = RivalIQ.metrics.new.MetricFormatter.formatDateInterval(newStartDate, newEndDate, timezone);
        }

        const isPartialDataPoint = metricResults[representativeRequestName].calculationMetadata?.domains?.[timeDimension]?.[timeDimensionValue]?.partialTimePeriod;

        const dataPoint = {
            idx: idx,
            xValue: newStartDate.format('MM/DD'),
            tooltipTitle: toolTipTitle,
            // need for onClick for ViewContentModal
            newStartDate: newStartDate.toISOString(),
            newEndDate: newEndDate.toISOString(),
            isPartialDataPoint: isPartialDataPoint
        };

        return _.reduce(metricKeys, (memo, metricKey) => {
            if (metricKeyFns?.[metricKey]) {
                memo[metricKey] = metricKeyFns[metricKey](result);
            } else {
                memo[metricKey] = _.last(result[metricKey]);
            }

            return memo;
        }, dataPoint);
    });

    return dataByInterval;
};

RIQMetricLineChart.propTypes = {
    chartKey: PropTypes.string.isRequired,
    lineChartHeight: PropTypes.number,
    dataByInterval: PropTypes.arrayOf(PropTypes.object).isRequired,
    chartConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
    tooltipContent: PropTypes.node
};

export default function RIQMetricLineChart({chartKey, lineChartHeight, dataByInterval, chartConfigs, tooltipContent}) {

    const isExporting = useIsExporting();
    const {activeTooltipIndex, onPointActivated, onTooltipFocusChange} = useHoverableTooltip();

    lineChartHeight ||= DEFAULT_LINE_CHART_HEIGHT;

    return (
        <>
            {_.map(chartConfigs, (chartConfig, chartConfigIdx) => {
                const tooltip = chartConfig.tooltipContent || tooltipContent;
                return (
                    <_MetricLineChart
                        key={chartConfigIdx}
                        chartKey={chartKey}
                        lineChartHeight={lineChartHeight}
                        dataByInterval={dataByInterval}
                        chartConfig={chartConfig}
                        tooltipPlacement={chartConfigIdx === 0 ? 'top' : 'bottom'}
                        activeTooltipIndex={activeTooltipIndex}
                        onPointActivated={onPointActivated}
                        onTooltipFocusChange={onTooltipFocusChange}
                        tooltipContent={tooltip}
                        {...isExporting}
                    />
                );
            })}
        </>
    );
}
